/**
 * What the next call will be placed with, as the dial pad's footer decides it.
 *
 * Two values, and they are not the same value. `wanted` is what the operator has
 * clicked; `effective` is what the call can actually do, which is `wanted` minus
 * the things that are not up to anybody: a transcript needs a recording to read
 * and a Gemini key to read it with, and an announcement needs a wording that
 * somebody has already written down.
 *
 * Keeping them apart is what makes the strip behave. If the toggles held only
 * the effective pair, switching recording off would erase the transcript choice,
 * and switching recording back on would leave the transcript stubbornly off -
 * the operator would have to click twice to undo one click.
 *
 * This mirrors `resolveCallFlags` in the API (`crm-calls/lib/call-flags.ts`), and
 * mirroring is all it is: the server clamps the same way on the way in, so a
 * client that got this wrong could still never place an impossible call.
 */
import type { NoticeLocale } from "../../lib/calls";


export interface DialFlags {
  /** Twilio records the call. */
  record: boolean;
  /** The after-call transcript and summary are produced from that recording. */
  transcribe: boolean;
  /** The spoken notice plays to the far end before the two sides are bridged. */
  announce: boolean;
  /**
   * Which language it is announced in. `null` is the account's base wording.
   *
   * `null` rather than a default locale, and this is the same distinction the
   * server draws: the base wording is whatever language the account wrote it
   * in, which is not necessarily Estonian. A dial pad that sent "et" whenever
   * nobody chose would be making a claim on the operator's behalf.
   */
  announceLocale: NoticeLocale | null;
  /**
   * The operator takes this call on their own mobile instead of in the browser.
   *
   * Not a property of the CALL the way the three above are - the customer hears
   * exactly the same announcement and the same recording either way - but it
   * belongs in the same set because it is decided in the same place, for the
   * same one call, and clamped by the same rules. It also starts `false` on
   * every pad: a call that quietly went to somebody's telephone because the
   * last one did would be a surprise in a corridor.
   */
  viaMobile: boolean;
}

/** What is possible at all, from the account settings. */
export interface DialCapabilities {
  /** `twilio.record` - the account's own answer, and this pill's starting point. */
  record: boolean;
  /** A Gemini key is configured. Without one nothing can be transcribed. */
  transcribeAvailable: boolean;
  /** An announcement is configured and switched on, so there is one to play. */
  noticeAvailable: boolean;
  /**
   * The live co-pilot is set up and its consent gate is not waived, so taking
   * the announcement off this one call takes the AI panel off it too.
   *
   * Here for the tooltip alone - nothing about the call changes shape. The
   * server refuses to process a call that announces nothing (`isPilotEnabled`),
   * and an operator who clicks the announcement off has to be told that in the
   * moment, not discover it as a panel that never fills in.
   */
  pilotNeedsNotice: boolean;
  /**
   * The languages this call could be announced in, beside the account's own.
   *
   * Empty on every account that has written no translation - which is what
   * makes the picker disappear rather than offer a list of one. Comes from the
   * server already filtered to the languages that would really change what this
   * caller hears; a language written only for a wording this call does not use
   * is not in here.
   */
  noticeLocales: { locale: NoticeLocale; label: string }[];
  /**
   * The account bridges calls as a CONFERENCE, so both parties join a room.
   *
   * The hard requirement behind "call from my mobile": a `<Dial><Number>`
   * bridge is one document with two legs and has nowhere to put a third
   * participant, so a telephone cannot be added to it. False takes the control
   * away entirely rather than disabling it - an operator cannot switch this on
   * from the pad, and a dead control they can never use is furniture.
   */
  conference: boolean;
  /**
   * The operator's OWN mobile, E.164, or "" when they have saved none.
   *
   * The only per-person field in a strip that is otherwise entirely about the
   * account, and the one the operator can change from here.
   */
  mobileNumber: string;
}

/** Can this operator take a call on their own phone at all? */
export function mobileAvailable(caps: DialCapabilities): boolean {
  return caps.conference && caps.mobileNumber.trim() !== "";
}

/** Does turning the announcement off for this call also silence the co-pilot? */
export function announceOffSilencesPilot(wanted: DialFlags, caps: DialCapabilities): boolean {
  return caps.pilotNeedsNotice && !wanted.announce;
}

/** What the pad starts on: the account's settings, everything possible switched on. */
export function initialDialFlags(caps: DialCapabilities): DialFlags {
  // The language starts unchosen, always, whatever translations exist. Opening
  // the pad on a language because it happens to be first in a list would put a
  // sentence on a customer's phone that nobody picked.
  // `viaMobile` starts false whatever is saved, and unlike the three above that
  // is not the account's default showing through - there is no account default
  // for it. Where a person is sitting is not a setting.
  return { record: caps.record, transcribe: true, announce: true, announceLocale: null, viaMobile: false };
}

/** The set that is actually sent with the call. */
export function effectiveDialFlags(wanted: DialFlags, caps: DialCapabilities): DialFlags {
  return {
    record: wanted.record,
    transcribe: wanted.record && caps.transcribeAvailable && wanted.transcribe,
    // Only ever clamped downwards. A call cannot announce a wording the account
    // does not have, so the pill goes dead rather than promising one.
    announce: wanted.announce && caps.noticeAvailable,
    // Clamped downwards like the pill above it, and to the BASE wording rather
    // than to silence: a language the account no longer has is a call announced
    // in the account's own words, never a call announced in none. A call that
    // announces nothing has no announcement language at all.
    announceLocale:
      wanted.announce &&
      caps.noticeAvailable &&
      wanted.announceLocale &&
      caps.noticeLocales.some((entry) => entry.locale === wanted.announceLocale)
        ? wanted.announceLocale
        : null,
    // Clamped downwards like everything else here, and the server clamps it
    // again: it re-reads the bridge mode and the saved number rather than
    // trusting the boolean, so a stale pad can ask for a mobile call and be
    // refused rather than place one against a number that has since been
    // cleared.
    viaMobile: wanted.viaMobile && mobileAvailable(caps),
  };
}

/** Why the transcript pill cannot be clicked, or `null` when it can. */
export type TranscribeBlocker = "no_key" | "no_recording" | null;

export function transcribeBlocker(wanted: DialFlags, caps: DialCapabilities): TranscribeBlocker {
  // The missing key is named first on purpose: it is the one the operator has to
  // go and fix in Settings, and it stays true whatever the recording pill says.
  if (!caps.transcribeAvailable) return "no_key";
  if (!wanted.record) return "no_recording";
  return null;
}

/** Why the announcement pill cannot be clicked, or `null` when it can. */
export type AnnounceBlocker = "no_notice" | null;

export function announceBlocker(caps: DialCapabilities): AnnounceBlocker {
  return caps.noticeAvailable ? null : "no_notice";
}
